import { useState } from "react";
import instance from "../../index";
import { PagedList } from "../../../models/paged-list";
import { BaseResponse } from "../../../models/base-response";
import {
    CreateProductCommand,
    ProductDto,
    ProductFilter,
    UpdateProductCommand
} from "../../../models/requests/product-filter";
import {ShortDto} from "../../../models/short-dto";

const useProductService = () => {
    const [loading, setLoading] = useState<boolean>(false);

    const handleRequest = async <T>(request: Promise<T>): Promise<T> => {
        setLoading(true);
        try {
            return await request;
        } finally {
            setLoading(false);
        }
    };

    async function createProduct(command: CreateProductCommand): Promise<ProductDto> {
        return handleRequest(instance.post<ProductDto>('/v1/backoffice/product-types', command).then(res => res.data));
    }

    async function updateProduct(id: string, command: UpdateProductCommand): Promise<ProductDto> {
        return handleRequest(instance.put<ProductDto>(`/v1/backoffice/product-types/${id}`, command).then(res => res.data));
    }

    async function deleteProduct(id: string): Promise<void> {
        return handleRequest(instance.delete(`/v1/backoffice/product-types/${id}`).then(() => {}));
    }

    async function activateProduct(id: string): Promise<ProductDto> {
        return handleRequest(instance.put<ProductDto>(`/v1/backoffice/product-types/${id}/activate`).then(res => res.data));
    }

    async function deactivateProduct(id: string): Promise<ProductDto> {
        return handleRequest(instance.put<ProductDto>(`/v1/backoffice/product-types/${id}/deactivate`).then(res => res.data));
    }

    async function getProducts(filter?: ProductFilter): Promise<PagedList<ProductDto>> {
        return handleRequest(instance.get<BaseResponse<PagedList<ProductDto>>>("/v1/backoffice/product-types", { params: filter })
            .then(res => res.data.data));
    }

    async function getProductById(id: string): Promise<ProductDto> {
        return handleRequest(instance.get<BaseResponse<ProductDto>>(`/v1/backoffice/product-types/${id}`).then(res => res.data.data));
    }

    async function getProductsShort(): Promise<ShortDto[]> {
        return handleRequest(instance.get<BaseResponse<ShortDto[]>>(`/v1/backoffice/product-types/short`).then(res => res.data.data));
    }

    return {
        loading,
        createProduct,
        updateProduct,
        deleteProduct,
        activateProduct,
        deactivateProduct,
        getProducts,
        getProductById,
        getProductsShort
    };
};

export default useProductService;
